import { z } from "zod";

import { dateString, optionalText } from "@/lib/forms/schema";

import type {
  ForeignNationalPassport,
  PassportPayload
} from "../../../../api/types/foreignNationalRecords";
import {
  dateInputToIso,
  normalizeText,
  toDateInputValue
} from "../../lib/format";

export type PassportDraft = {
  passportNumber: string;
  issuingCountry: string;
  issueDate: string;
  expirationDate: string;
  isCurrent: boolean;
  notes: string;
};

export const emptyPassportDraft = (): PassportDraft => ({
  passportNumber: "",
  issuingCountry: "",
  issueDate: "",
  expirationDate: "",
  isCurrent: true,
  notes: ""
});

export const passportToDraft = (p: ForeignNationalPassport): PassportDraft => ({
  passportNumber: normalizeText(p.passport_number),
  issuingCountry: normalizeText(p.issuing_country),
  issueDate: toDateInputValue(p.issue_date),
  expirationDate: toDateInputValue(p.expiration_date),
  isCurrent: Boolean(p.is_current),
  notes: normalizeText(p.notes)
});

/** A passport needs a number or an expiry; it can't expire before it's issued. */
export const passportDraftSchema: z.ZodType<PassportDraft, PassportDraft> = z
  .object({
    passportNumber: optionalText(),
    issuingCountry: optionalText(),
    issueDate: dateString(),
    expirationDate: dateString(),
    isCurrent: z.boolean(),
    notes: optionalText()
  })
  .superRefine((d, ctx) => {
    if (!normalizeText(d.passportNumber) && !normalizeText(d.expirationDate)) {
      ctx.addIssue({
        code: "custom",
        path: ["passportNumber"],
        message: "Add the passport number or its expiration date."
      });
    }
    if (d.issueDate && d.expirationDate && d.expirationDate < d.issueDate) {
      ctx.addIssue({
        code: "custom",
        path: ["expirationDate"],
        message: "Expiration can't be before the issue date."
      });
    }
  });

export const passportDraftToPayload = (d: PassportDraft): PassportPayload => ({
  passport_number:
    normalizeText(d.passportNumber).replace(/\s+/g, "").toUpperCase() || null,
  issuing_country: normalizeText(d.issuingCountry) || null,
  issue_date: dateInputToIso(d.issueDate),
  expiration_date: dateInputToIso(d.expirationDate),
  is_current: d.isCurrent,
  notes: normalizeText(d.notes) || null
});

const ms = (v?: string | null) => {
  const n = Date.parse(v ?? "");
  return Number.isFinite(n) ? n : 0;
};

/** Current first, then latest expiry. */
export const sortPassports = (
  a: ForeignNationalPassport,
  b: ForeignNationalPassport
): number =>
  Number(b.is_current) - Number(a.is_current) ||
  ms(b.expiration_date) - ms(a.expiration_date);

export type PassportState = "current" | "expired" | "expiring" | "previous";

/** Expiring = within 270 days, the earliest passport reminder tier. */
export const passportState = (
  p: ForeignNationalPassport,
  now: Date = new Date()
): PassportState => {
  const exp = ms(p.expiration_date);
  if (exp && exp < now.getTime()) return "expired";
  if (!p.is_current) return "previous";
  if (exp && exp - now.getTime() < 270 * 86_400_000) return "expiring";
  return "current";
};

export const passportFocusDomId = (id: string): string => `passport-${id}`;
